import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { useRevalidator } from 'react-router';
import { isExampleResource } from '@gonfalon/analytics/isExampleResource';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { preserveExperimentFormValuesAfterSave } from '@gonfalon/dogfood-flags/dogfood-flags';
import { isRESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { convertExperimentAndLayerConfigToFormProps } from '@gonfalon/experiments/draft-experiment-form';
import { isDraftExperiment } from '@gonfalon/experiments/isDraftExperiment';
import type { DraftExperimentFormProps, DraftExperimentLayerConfig, Experiment } from '@gonfalon/experiments/types';
import {
  useExperimentFieldMutability,
  type UseFieldMutabilityResult,
} from '@gonfalon/experiments/useExperimentFieldMutability';
import { useTypedAppForm } from '@gonfalon/forms-experimental/hooks';
import { msg } from '@gonfalon/messages/msg';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { toastQueue } from '@launchpad-ui/components';

import type { UpdateDraftExperimentOptions } from '#components/experimentation/common/hooks/useDraftExperimentActionCallbacks';
import { useDraftExperimentActionCallbacks } from '#components/experimentation/common/hooks/useDraftExperimentActionCallbacks';
import { snackbarAssignmentSourceProbe } from '#components/experimentation/experiments/draft-experiment/ExperimentDesignHealthCheck/snackbarAssignmentSourceProbe';
import { trackExperimentEvent, trackMABEvent } from '#utils/analyticsUtils';

export type DraftExperimentFormContextValue = {
  form: ReturnType<typeof useTypedAppForm<DraftExperimentFormProps>>;
  experiment?: Experiment;
  isDraftExperiment: boolean;
  isMAB: boolean;
  defaultValues: DraftExperimentFormProps;
  /** Field mutability utilities based on experiment status and API permissions */
  fieldMutability: UseFieldMutabilityResult;
  /**
   * Set options for the next form submission (e.g., saveAndStart for running experiments).
   * Call this before form.handleSubmit() to pass options to the onSubmit handler.
   */
  setSubmitOptions: (options: UpdateDraftExperimentOptions | undefined) => void;
};

export const DraftExperimentFormContext = createContext<DraftExperimentFormContextValue | undefined>(undefined);

export const useDraftExperimentForm = ({
  experiment,
  layerConfig,
}: {
  experiment?: Experiment;
  layerConfig?: DraftExperimentLayerConfig;
}): DraftExperimentFormContextValue => {
  const revalidator = useRevalidator();
  const experimentKey = experiment?.key ?? '';
  const projectKey = useProjectKey();
  const environmentKey = useSelectedEnvironmentKey();
  const defaultValues: DraftExperimentFormProps = useMemo(
    () => convertExperimentAndLayerConfigToFormProps(experiment ?? ({} as Experiment), layerConfig),
    [experiment, layerConfig],
  );

  // Ref to store options for the next form submission (used for running experiment save+start flow)
  const submitOptionsRef = useRef<UpdateDraftExperimentOptions | undefined>(undefined);

  const { updateDraftExperimentWithIteration } = useDraftExperimentActionCallbacks({
    projectKey,
    environmentKey,
    experimentKey,
    experiment,
    defaultValues,
  });

  const form = useTypedAppForm<DraftExperimentFormProps>({
    defaultValues,
    onSubmit: async ({ value }: { value: DraftExperimentFormProps }) => {
      const trackEvent = value.isMAB ? trackMABEvent : trackExperimentEvent;
      // Get and clear the submit options from ref
      const options = submitOptionsRef.current;
      submitOptionsRef.current = undefined;
      try {
        const updatedExperiment = await updateDraftExperimentWithIteration(value, options);
        const includesStart = options?.saveAndStart;
        trackEvent(includesStart ? 'Saving draft form with start' : 'Saving draft form', {
          sampleSize: value.iterationProps?.experimentAllocation,
          frequency: value.iterationProps?.reallocationFrequencyHours,
          name: value.experimentName,
          [value.isMAB ? 'goal' : 'hypothesis']: value.hypothesis,
          randomizationUnit: value.iterationProps?.randomizationUnit,
          layer: value.layerConfig?.name,
          holdout: value.holdoutId,
          flagName: value.iterationProps?.flagConfig?.flagKey,
          ruleName: value.iterationProps?.flagConfig?.targetingRule,
          notInExperimentVariationId: value.iterationProps?.flagConfig?.notInExperimentVariationId,
          ...(includesStart && { saveAndStart: true }),
          isExampleResource: isExampleResource(value.experimentKey),
        });
        form.reset(preserveExperimentFormValuesAfterSave() ? value : defaultValues);
        toastQueue.add({
          title: includesStart
            ? msg('experiments.experimentFormToast.updatedAndStarted', { isMab: value.isMAB ? 'true' : 'false' })
            : msg('experiments.experimentFormToast.updated', { isMab: value.isMAB ? 'true' : 'false' }),
          status: 'success',
        });
        snackbarAssignmentSourceProbe(updatedExperiment?.assignmentSourceProbe);
        await revalidator.revalidate();
      } catch (error) {
        toastQueue.add({
          description: isRESTAPIError(error)
            ? error.status === 403
              ? msg('experiments.experimentFormToast.updateForbidden', { isMab: value.isMAB ? 'true' : 'false' })
              : error?.message
            : msg('experiments.experimentFormToast.updateError', { isMab: value.isMAB ? 'true' : 'false' }),
          status: 'error',
        });
        // Re-throw so caller knows about the error
        throw error;
      }
    },
  });

  const isDraft = useMemo(() => isDraftExperiment(experiment), [experiment]);
  const isMAB = experiment?.type === 'mab';
  const fieldMutability = useExperimentFieldMutability(experiment, isDraft);

  const setSubmitOptions = useCallback((options: UpdateDraftExperimentOptions | undefined) => {
    submitOptionsRef.current = options;
  }, []);

  return useMemo(
    () => ({
      form,
      experiment,
      isDraftExperiment: isDraft,
      isMAB,
      defaultValues,
      fieldMutability,
      setSubmitOptions,
    }),
    [form, experiment, isDraft, isMAB, defaultValues, fieldMutability, setSubmitOptions],
  );
};

export const useDraftExperimentFormContext = () => {
  const context = useContext(DraftExperimentFormContext);
  if (!context) {
    throw new Error('useDraftExperimentForm must be used within a DraftExperimentProvider');
  }
  return context;
};
